import React, { useEffect, useState } from 'react';
import { ActivityIcon, Building2Icon, HospitalIcon, HotelIcon, HouseIcon, NetworkIcon, PlaneIcon, UniversityIcon } from 'lucide-react';
type Endpoint = {
  label: string;
  Icon: React.ElementType;
};
const endpoints: Endpoint[] = [{
  label: 'Office',
  Icon: Building2Icon
}, {
  label: 'Hotel',
  Icon: HotelIcon
}, {
  label: 'Gym',
  Icon: ActivityIcon
}, {
  label: 'University',
  Icon: UniversityIcon
}, {
  label: 'Hospital',
  Icon: HospitalIcon
}, {
  label: 'Apartment',
  Icon: HouseIcon
}, {
  label: 'Airport',
  Icon: PlaneIcon
}];
const RADIUS = 37;
function nodePosition(index: number, total: number) {
  const angle = (-90 + index * 360 / total) * (Math.PI / 180);
  return {
    left: 50 + RADIUS * Math.cos(angle),
    top: 50 + RADIUS * Math.sin(angle)
  };
}
export function EndpointNetwork() {
  const total = endpoints.length;
  const [connected, setConnected] = useState(0);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setConnected(total);
      return;
    }
    const interval = window.setInterval(() => {
      setConnected(current => {
        if (current >= total) {
          window.clearInterval(interval);
          return current;
        }
        return current + 1;
      });
    }, 700);
    return () => window.clearInterval(interval);
  }, [total]);
  useEffect(() => {
    const showFinalState = () => setConnected(total);
    window.addEventListener('beforeprint', showFinalState);
    return () => window.removeEventListener('beforeprint', showFinalState);
  }, [total]);
  const allConnected = connected >= total;
  return <figure className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-4 sm:p-5" aria-labelledby="endpoint-network-caption" data-id="element-962" data-mp="3c1pzt9vkoji3">
      <div className="relative mx-auto aspect-square w-full max-w-[430px]" data-id="element-963" data-mp="3lzx4at8od937">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" aria-hidden="true" data-id="element-964" data-mp="3q0phfxko3y4r">
          <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="#dbeafe" strokeWidth="0.7" strokeDasharray="2.5 2.5" data-id="element-965" data-mp="1qvz6hmzibt5n" />
          {endpoints.map((endpoint, index) => {
          const {
            left,
            top
          } = nodePosition(index, total);
          const isLive = index < connected;
          return <line key={`spoke-${endpoint.label}`} x1="50" y1="50" x2={left} y2={top} stroke={isLive ? '#2563eb' : '#e2e8f0'} strokeWidth={isLive ? 0.9 : 0.6} strokeLinecap="round" className="transition-all duration-500" data-id="element-966" data-mp="1uxkkxcrowfhy" data-mpi={index} />;
        })}
          {endpoints.map((endpoint, index) => {
          const from = nodePosition(index, total);
          const to = nodePosition((index + 1) % total, total);
          const isLive = index < connected - 1 || allConnected;
          return <line key={`ring-${endpoint.label}`} x1={from.left} y1={from.top} x2={to.left} y2={to.top} stroke={isLive ? '#60a5fa' : 'transparent'} strokeWidth="0.9" strokeLinecap="round" className="transition-all duration-500" data-id="element-967" data-mp="k93o9jhq22gx" data-mpi={index} />;
        })}
          <circle className={allConnected ? 'pd3-net-orbit' : 'pd3-net-orbit pd3-net-orbit--dim'} cx="50" cy="50" r={RADIUS} fill="none" stroke="#2563eb" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="14 218" pathLength="232" data-id="element-968" data-mp="5arbzs3mn6k2" />
        </svg>

        <div className={`pd3-net-hub absolute left-1/2 top-1/2 flex h-[28%] w-[28%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 transition-colors duration-500 ${allConnected ? 'border-blue-400 bg-blue-500' : 'border-blue-200 bg-navy-950'}`} data-id="element-969" data-mp="3ijxe087cc8pn">
          <NetworkIcon className={`h-7 w-7 transition-colors duration-500 ${allConnected ? 'text-white' : 'text-blue-300'}`} aria-hidden="true" data-id="element-970" data-mp="3ctugrnsy6v14" />
        </div>

        {endpoints.map(({
        label,
        Icon
      }, index) => {
        const {
          left,
          top
        } = nodePosition(index, total);
        const isLive = index < connected;
        return <div key={label} style={{
          left: `${left}%`,
          top: `${top}%`
        }} className={`absolute flex w-[27%] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1 rounded-xl border px-2 py-2 shadow-sm transition-all duration-500 ${isLive ? 'scale-[1.04] border-blue-400 bg-blue-500 text-white shadow-[0_10px_24px_rgba(37,99,235,.28)]' : 'border-slate-200 bg-white text-navy-950'}`} data-id="element-971" data-mp="2yxh2enjpn9qv" data-mpi={index}>
              <Icon className={`h-4 w-4 transition-colors duration-500 ${isLive ? 'text-white' : 'text-blue-500'}`} aria-hidden="true" data-id="element-972" data-mp="2b636y8q9iy1o" />
              <p className="text-[10px] font-bold leading-none sm:text-[11px]" data-id="element-973" data-mp="267ifoysznopd">{label}</p>
            </div>;
      })}
      </div>

      <figcaption id="endpoint-network-caption" className={`relative mt-3 rounded-xl p-3 text-center text-sm font-bold transition-colors duration-500 ${allConnected ? 'bg-blue-600 text-white' : 'bg-navy-950 text-white'}`} data-id="element-974" data-mp="qnixssjq30ua">
        Every everyday location can become a connected endpoint.
      </figcaption>
    </figure>;
}